import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Calendar, Clock, MapPin, Search, Plus, Filter, CheckCircle, AlertCircle } from "lucide-react";

export default function Bookings() {
  return (
    <div>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Bookings Management</h2>
            <p className="text-muted-foreground">Manage visitor bookings and facility reservations</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="button-filter">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
            <Button data-testid="button-new-booking">
              <Plus className="w-4 h-4 mr-2" />
              New Booking
            </Button>
          </div>
        </div>

        {/* Booking Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card data-testid="card-confirmed-bookings">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Confirmed Bookings</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">34</div>
              <p className="text-xs text-muted-foreground">Today</p>
            </CardContent>
          </Card>

          <Card data-testid="card-pending-bookings">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Bookings</CardTitle>
              <Clock className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">7</div>
              <p className="text-xs text-muted-foreground">Awaiting approval</p>
            </CardContent>
          </Card>

          <Card data-testid="card-in-progress">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">In Progress</CardTitle>
              <MapPin className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">12</div>
              <p className="text-xs text-muted-foreground">Currently on site</p>
            </CardContent>
          </Card>

          <Card data-testid="card-completed">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <CheckCircle className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">89</div>
              <p className="text-xs text-muted-foreground">This week</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Search Bookings</CardTitle>
            <CardDescription>Find specific bookings by visitor, company, or booking reference</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search bookings..." 
                    className="pl-10"
                    data-testid="input-search-bookings"
                  />
                </div>
              </div>
              <Button variant="outline" data-testid="button-search">
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bookings Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Bookings</CardTitle>
            <CardDescription>Visitor facility bookings and their current status</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Booking ID</TableHead>
                  <TableHead>Visitor</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  {
                    id: "BK001",
                    visitor: "John Smith",
                    company: "ABC Transport",
                    facility: "Weighbridge A",
                    dateTime: "2024-09-21 09:00",
                    duration: "2 hours",
                    status: "confirmed"
                  },
                  {
                    id: "BK002",
                    visitor: "Maria Garcia",
                    company: "Quick Logistics",
                    facility: "Loading Bay 3",
                    dateTime: "2024-09-21 10:30",
                    duration: "1.5 hours",
                    status: "in-progress"
                  },
                  {
                    id: "BK003",
                    visitor: "David Chen",
                    company: "Freight Masters",
                    facility: "Weighbridge B",
                    dateTime: "2024-09-21 14:00",
                    duration: "45 minutes",
                    status: "pending"
                  },
                  {
                    id: "BK004",
                    visitor: "Sarah Johnson",
                    company: "Metro Delivery",
                    facility: "Parking Bay 5",
                    dateTime: "2024-09-21 08:00",
                    duration: "3 hours",
                    status: "completed"
                  },
                  {
                    id: "BK005",
                    visitor: "Michael Brown",
                    company: "City Haulage",
                    facility: "Fuel Station",
                    dateTime: "2024-09-21 16:00",
                    duration: "30 minutes",
                    status: "cancelled"
                  }
                ].map((booking) => (
                  <TableRow key={booking.id} data-testid={`row-booking-${booking.id}`}>
                    <TableCell className="font-medium">{booking.id}</TableCell>
                    <TableCell>{booking.visitor}</TableCell>
                    <TableCell>{booking.company}</TableCell>
                    <TableCell>{booking.facility}</TableCell>
                    <TableCell>{booking.dateTime}</TableCell>
                    <TableCell>{booking.duration}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={
                          booking.status === "confirmed" ? "default" :
                          booking.status === "in-progress" ? "secondary" :
                          booking.status === "pending" ? "outline" :
                          booking.status === "completed" ? "outline" : "destructive"
                        }
                        data-testid={`badge-status-${booking.id}`}
                      >
                        {booking.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" data-testid={`button-view-${booking.id}`}>
                          View
                        </Button>
                        {booking.status === "pending" && (
                          <Button variant="ghost" size="sm" data-testid={`button-approve-${booking.id}`}>
                            Approve
                          </Button>
                        )}
                        {booking.status === "confirmed" && (
                          <Button variant="ghost" size="sm" data-testid={`button-checkin-${booking.id}`}>
                            Check In
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Today's Schedule
              </CardTitle>
              <CardDescription>Upcoming bookings for today</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-medium">09:00 - Weighbridge A</p>
                    <p className="text-sm text-muted-foreground">John Smith (ABC Transport)</p>
                  </div>
                  <Badge variant="default" data-testid="badge-schedule-1">Confirmed</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium">10:30 - Loading Bay 3</p>
                    <p className="text-sm text-muted-foreground">Maria Garcia (Quick Logistics)</p>
                  </div>
                  <Badge variant="secondary" data-testid="badge-schedule-2">In Progress</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg">
                  <div>
                    <p className="font-medium">14:00 - Weighbridge B</p>
                    <p className="text-sm text-muted-foreground">David Chen (Freight Masters)</p>
                  </div>
                  <Badge variant="outline" data-testid="badge-schedule-3">Pending</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Alerts & Notifications
              </CardTitle>
              <CardDescription>Important booking alerts and reminders</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-2 bg-red-50 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-700">Overdue Check-in</p>
                    <p className="text-sm text-red-600">BK002 should have checked in 15 minutes ago</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-yellow-50 rounded-lg">
                  <Clock className="h-4 w-4 text-yellow-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-700">Upcoming Booking</p>
                    <p className="text-sm text-yellow-600">BK003 scheduled to arrive in 30 minutes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-2 bg-blue-50 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-700">Facility Available</p>
                    <p className="text-sm text-blue-600">Weighbridge A is now available for booking</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}